// =============================================================
// Loads drop data from /drops.json, renders the countdown card +
// the creator cards in the "Who's wanted" carousel, and ticks the
// countdown to the next upcoming drop every second.
// =============================================================

type SocialPlatform = "instagram" | "tiktok" | "youtube";

type CreatorSocial = {
  platform: SocialPlatform;
  url: string;
};

type DropCreator = {
  name: string;
  handle: string;
  avatar: string;
  socials: CreatorSocial[];
};

const SOCIAL_META: Record<SocialPlatform, { label: string; icon: string }> = {
  instagram: { label: "Instagram", icon: "images/icon-instagram.svg" },
  tiktok: { label: "TikTok", icon: "images/icon-tiktok.svg" },
  youtube: { label: "YouTube", icon: "images/icon-youtube.svg" },
};

type Drop = {
  id: string;
  city: string;
  dropLabel: string;
  date: string;
  timeTBA?: boolean;
  creator: DropCreator;
  recapUrl?: string;
};

type DropsPayload = {
  drops: Drop[];
};

const DAY_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
});

function pad(value: number): string {
  return value.toString().padStart(2, "0");
}

function formatDropDay(iso: string): string {
  // Date only, no time — used while a drop's start time is still TBA.
  return DAY_FORMATTER.format(new Date(iso));
}

function renderCountdown(target: Date, root: HTMLElement) {
  const totalSeconds = Math.max(0, Math.floor((target.getTime() - Date.now()) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const hoursEl = root.querySelector<HTMLElement>('[data-unit="hours"]');
  const minutesEl = root.querySelector<HTMLElement>('[data-unit="minutes"]');
  const secondsEl = root.querySelector<HTMLElement>('[data-unit="seconds"]');
  if (hoursEl) {
    hoursEl.textContent = pad(hours);
  }
  if (minutesEl) {
    minutesEl.textContent = pad(minutes);
  }
  if (secondsEl) {
    secondsEl.textContent = pad(seconds);
  }
}

function renderCountdownTBA(drop: Drop, root: HTMLElement) {
  // No confirmed time yet — swap the ticking digits for the drop day + "Time TBA".
  const countdown = root.querySelector<HTMLElement>(".drop-card__countdown");
  if (countdown) {
    countdown.innerHTML = `<p class="drop-card__tba">${formatDropDay(drop.date)} · Time TBA</p>`;
  }
}

function buildCreatorCard(drop: Drop): HTMLElement {
  const article = document.createElement("article");
  article.className = "creator-card glass-card";
  article.dataset.dropId = drop.id;
  article.setAttribute("role", "listitem");

  const socialsMarkup = drop.creator.socials
    .map((social) => {
      const meta = SOCIAL_META[social.platform];
      return `
        <a href="${social.url}" aria-label="${meta.label}" target="_blank" rel="noopener noreferrer">
          <img src="${meta.icon}" alt="" />
        </a>`;
    })
    .join("");

  article.innerHTML = `
    <div class="creator-card__inner">
      <figure class="creator-card__avatar">
        <img src="${drop.creator.avatar}" alt="${drop.creator.name}" />
      </figure>
      <div class="creator-card__socials">${socialsMarkup}
      </div>
      <div class="creator-card__meta">
        <h3 class="creator-card__name">${drop.creator.name}</h3>
        <p class="creator-card__handle">${drop.creator.handle}</p>
      </div>
    </div>
  `;

  return article;
}

function renderCreatorCards(drops: Drop[], container: HTMLElement) {
  container.replaceChildren(...drops.map((drop) => buildCreatorCard(drop)));
}

function ordinalSuffix(day: number): string {
  const lastTwoDigits = day % 100;
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return "th";
  }
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

function formatDropDateLabel(iso: string): string {
  // "Drop on June 12th" — every drop shares one date, shown once below the grid.
  const date = new Date(iso);
  return `Drop on ${formatDropDay(iso)}${ordinalSuffix(date.getDate())}`;
}

async function init() {
  const response = await fetch("/drops.json");
  if (!response.ok) {
    throw new Error(`Failed to load drops.json: ${response.status}`);
  }
  const payload = (await response.json()) as DropsPayload;

  const sortedByDate = [...payload.drops].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
  const now = Date.now();
  const nextDrop = sortedByDate.find((drop) => new Date(drop.date).getTime() > now);

  const dropCard = document.querySelector<HTMLElement>(".drop-card");
  if (dropCard && nextDrop) {
    if (nextDrop.timeTBA) {
      renderCountdownTBA(nextDrop, dropCard);
    } else {
      const target = new Date(nextDrop.date);
      renderCountdown(target, dropCard);
      window.setInterval(() => renderCountdown(target, dropCard), 1000);
    }
  }

  const wantedCards = document.querySelector<HTMLElement>(".section-wanted__cards");
  if (wantedCards) {
    renderCreatorCards(sortedByDate, wantedCards);
  }

  const dropDateLabel = document.querySelector<HTMLElement>(".section-wanted__drop-date");
  const labelDrop = nextDrop ?? sortedByDate[0];
  if (dropDateLabel && labelDrop) {
    dropDateLabel.textContent = formatDropDateLabel(labelDrop.date);
  }
}

void init();
